import { CheckCircle, Download, HardDrive, RefreshCw, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { useVelociraptorSettings } from '../../hooks'
import { formatFileSize } from '../../services/velociraptor-settings'
import type { TestCollectorResult, VelociraptorConfig } from '../../types/velociraptor-settings'
import { DEFAULT_GUI_PORT } from './constants'
import { SectionCard } from './SectionCard'

type TestCollectorState = ReturnType<typeof useVelociraptorSettings>['testCollector']

interface TargetOsSelectProps {
  value: string
  disabled: boolean
  onChange: (value: string) => void
}

function TargetOsSelect({ value, disabled, onChange }: TargetOsSelectProps): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-stone-700 dark:text-stone-300">
        {t('velociraptorSettings.collector.targetOs')}
      </label>
      <select
        value={value}
        onChange={(ev) => {
          onChange(ev.target.value)
        }}
        disabled={disabled}
        className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 focus:border-[#4e5e82] focus:ring-2 focus:ring-[#4e5e82] disabled:opacity-50 dark:border-stone-600 dark:bg-stone-700 dark:text-stone-100"
      >
        <option value="Windows">{t('velociraptorSettings.os.windowsX64')}</option>
        <option value="Windows_x86">{t('velociraptorSettings.os.windowsX86')}</option>
        <option value="Linux">{t('velociraptorSettings.os.linuxX64')}</option>
        <option value="MacOS">{t('velociraptorSettings.os.macOsIntel')}</option>
        <option value="MacOS_arm">{t('velociraptorSettings.os.macOsArm')}</option>
      </select>
    </div>
  )
}

interface QuickTriageToggleProps {
  checked: boolean
  disabled: boolean
  onChange: (checked: boolean) => void
}

function QuickTriageToggle({
  checked,
  disabled,
  onChange,
}: QuickTriageToggleProps): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div className="flex items-center gap-3">
      <input
        type="checkbox"
        id="quickTriage"
        checked={checked}
        onChange={(ev) => {
          onChange(ev.target.checked)
        }}
        disabled={disabled}
        className="h-4 w-4 rounded border-stone-300 text-[#5c6e94] focus:ring-[#4e5e82]"
      />
      <label htmlFor="quickTriage" className="text-sm text-stone-700 dark:text-stone-300">
        {t('velociraptorSettings.collector.quickTriageMode')}
      </label>
    </div>
  )
}

interface GenerateButtonProps {
  generating: boolean
  onGenerate: () => void
}

function GenerateButton({ generating, onGenerate }: GenerateButtonProps): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <button
      onClick={onGenerate}
      disabled={generating}
      className="inline-flex items-center gap-2 rounded-lg bg-[#5c6e94] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#5c6e94] disabled:opacity-50"
    >
      {generating ? (
        <>
          <RefreshCw className="h-4 w-4 animate-spin" />
          {t('velociraptorSettings.generating')}
        </>
      ) : (
        <>
          <HardDrive className="h-4 w-4" />
          {t('velociraptorSettings.generateTestCollector')}
        </>
      )}
    </button>
  )
}

interface CollectorErrorBannerProps {
  error: string | null
}

function CollectorErrorBanner({ error }: CollectorErrorBannerProps): React.ReactElement | null {
  const { t } = useTranslation('admin')
  if (!error) return null
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
      <div className="flex items-start gap-3">
        <XCircle className="mt-0.5 h-5 w-5 text-red-600 dark:text-red-400" />
        <div>
          <p className="text-sm font-medium text-red-800 dark:text-red-200">
            {t('velociraptorSettings.collector.generationFailed')}
          </p>
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      </div>
    </div>
  )
}

interface CollectorRowProps {
  label: string
  value: React.ReactNode
}

function CollectorRow({ label, value }: CollectorRowProps): React.ReactElement {
  return (
    <div className="flex justify-between">
      <span className="text-stone-500 dark:text-stone-400">{label}</span>
      <span>{value}</span>
    </div>
  )
}

interface CollectorDetailsProps {
  result: TestCollectorResult
}

function CollectorDetails({ result }: CollectorDetailsProps): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div className="mt-2 grid gap-2 text-xs">
      <CollectorRow
        label={t('velociraptorSettings.collector.targetOsLabel')}
        value={<span className="font-mono">{result.targetOs}</span>}
      />
      {result.flowId !== null && (
        <CollectorRow
          label={t('velociraptorSettings.collector.flowId')}
          value={<span className="font-mono">{result.flowId}</span>}
        />
      )}
      {result.collectorSizeBytes !== null && (
        <CollectorRow
          label={t('velociraptorSettings.collector.size')}
          value={<span>{formatFileSize(result.collectorSizeBytes)}</span>}
        />
      )}
      {result.collectorHash !== null && (
        <CollectorRow
          label={t('velociraptorSettings.collector.sha256')}
          value={<span className="font-mono text-[10px] break-all">{result.collectorHash}</span>}
        />
      )}
      {result.collectorPath !== null && (
        <CollectorRow
          label={t('velociraptorSettings.collector.path')}
          value={<span className="font-mono text-[10px] break-all">{result.collectorPath}</span>}
        />
      )}
      {result.artefactsIncluded.length > 0 && (
        <div className="mt-2">
          <span className="mb-1 block text-stone-500 dark:text-stone-400">
            {t('velociraptorSettings.collector.artefactsIncluded')}
          </span>
          <ul className="list-inside list-disc text-stone-700 dark:text-stone-300">
            {result.artefactsIncluded.map((artefact) => (
              <li key={artefact} className="font-mono">
                {artefact}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

interface DownloadCollectorButtonProps {
  downloading: boolean
  onDownload: () => void
}

function DownloadCollectorButton({
  downloading,
  onDownload,
}: DownloadCollectorButtonProps): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div className="mt-4 border-t border-green-200 pt-3 dark:border-green-800">
      <button
        onClick={onDownload}
        disabled={downloading}
        className="inline-flex items-center gap-2 rounded-lg bg-[#5c6e94] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#5c6e94] disabled:opacity-50"
      >
        {downloading ? (
          <>
            <RefreshCw className="h-4 w-4 animate-spin" />
            {t('velociraptorSettings.collector.downloading')}
          </>
        ) : (
          <>
            <Download className="h-4 w-4" />
            {t('velociraptorSettings.downloadCollector')}
          </>
        )}
      </button>
    </div>
  )
}

interface CollectorResultPanelProps {
  result: TestCollectorResult
  downloading: boolean
  onDownload: () => void
}

function CollectorResultPanel({
  result,
  downloading,
  onDownload,
}: CollectorResultPanelProps): React.ReactElement {
  const { t } = useTranslation('admin')
  const containerClass = result.success
    ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
    : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
  const titleClass = result.success
    ? 'text-green-800 dark:text-green-200'
    : 'text-red-800 dark:text-red-200'
  const headline = result.success
    ? t('velociraptorSettings.collector.collectorGenerated')
    : t('velociraptorSettings.collector.generationFailed')
  return (
    <div className={`rounded-lg p-4 ${containerClass}`}>
      <div className="flex items-start gap-3">
        {result.success ? (
          <CheckCircle className="mt-0.5 h-5 w-5 text-green-600 dark:text-green-400" />
        ) : (
          <XCircle className="mt-0.5 h-5 w-5 text-red-600 dark:text-red-400" />
        )}
        <div className="flex-1">
          <p className={`text-sm font-medium ${titleClass}`}>{headline}</p>
          {result.success && <CollectorDetails result={result} />}
          {result.errorMessage !== null && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">{result.errorMessage}</p>
          )}
          {result.success && result.flowId !== null && (
            <DownloadCollectorButton downloading={downloading} onDownload={onDownload} />
          )}
        </div>
      </div>
    </div>
  )
}

interface CollectorHelpTextProps {
  guiPort: number
}

function CollectorHelpText({ guiPort }: CollectorHelpTextProps): React.ReactElement {
  const { t } = useTranslation('admin')
  const guiUrl = `https://localhost:${String(guiPort)}`
  return (
    <p className="text-xs text-stone-500 dark:text-stone-400">
      {t('velociraptorSettings.collector.helpText')}{' '}
      <a
        href={guiUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#5c6e94] hover:underline dark:text-[#8a9bba]"
      >
        {t('velociraptorSettings.collector.velociraptorGui')}
      </a>{' '}
      {t('velociraptorSettings.collector.serverArtefacts')}
    </p>
  )
}

export interface TestCollectorCardProps {
  testCollector: TestCollectorState
  config: VelociraptorConfig | null
  testCollectorOs: string
  setTestCollectorOs: (value: string) => void
  testCollectorQuickTriage: boolean
  setTestCollectorQuickTriage: (value: boolean) => void
  onGenerate: () => void
  onDownload: () => void
}

export function TestCollectorCard(props: TestCollectorCardProps): React.ReactElement {
  const { t } = useTranslation('admin')
  const guiPort = props.config?.guiPort ?? DEFAULT_GUI_PORT
  return (
    <SectionCard
      title={t('velociraptorSettings.testCollector')}
      description={t('velociraptorSettings.testCollectorDesc')}
      icon={HardDrive}
    >
      <div className="space-y-4">
        <TargetOsSelect
          value={props.testCollectorOs}
          disabled={props.testCollector.generating}
          onChange={props.setTestCollectorOs}
        />
        <QuickTriageToggle
          checked={props.testCollectorQuickTriage}
          disabled={props.testCollector.generating}
          onChange={props.setTestCollectorQuickTriage}
        />
        <GenerateButton generating={props.testCollector.generating} onGenerate={props.onGenerate} />
        <CollectorErrorBanner error={props.testCollector.error} />
        {props.testCollector.result && (
          <CollectorResultPanel
            result={props.testCollector.result}
            downloading={props.testCollector.downloading}
            onDownload={props.onDownload}
          />
        )}
        <CollectorHelpText guiPort={guiPort} />
      </div>
    </SectionCard>
  )
}
